(() => {
  const DRAFT_KEY = 'furAndFarPetPassportDraftV3';
  const PET_FIELDS = new Set([
    'id', 'pet_name', 'pet_type', 'breed', 'birthday', 'weight_kg',
    'size_category', 'harness_size', 'clothing_size', 'chest_cm', 'neck_cm',
    'home_town', 'travel_style', 'travel_bed', 'passport_status', 'likes',
    'travel_notes',
  ]);

  class PassportStateError extends Error {
    constructor(code) {
      super(code);
      this.name = 'PassportStateError';
      this.code = code;
    }
  }

  const createStore = ({ api, isSignedIn, sessionStorage, maxProfiles = 10 }) => {
    let status = isSignedIn ? 'idle' : 'guest_empty';
    let pets = [];
    let wishlists = [];
    let conflict = null;

    const applyState = (result) => {
      if (!result || result.ok !== true || !result.data || !Array.isArray(result.data.pets)) {
        throw new PassportStateError('invalid_server_state');
      }
      pets = result.data.pets.slice(0, maxProfiles);
      wishlists = Array.isArray(result.data.wishlists) ? result.data.wishlists : [];
      status = 'ready';
      conflict = null;
    };

    const snapshot = () => ({
      status,
      pets: pets.slice(),
      wishlists: wishlists.slice(),
      conflict,
    });

    const load = async () => {
      if (!isSignedIn) {
        let draft = null;
        try {
          draft = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || 'null');
        } catch (error) {
          sessionStorage.removeItem(DRAFT_KEY);
        }
        status = draft && draft.pet_name ? 'guest_editing' : 'guest_empty';
        return snapshot();
      }
      status = 'loading';
      try {
        applyState(await api.state());
      } catch (error) {
        status = 'error';
        throw error;
      }
      return snapshot();
    };

    const petPayload = (pet, consent) => {
      const payload = {};
      Object.entries(pet || {}).forEach(([key, value]) => {
        const normalizedKey = key === 'weight' ? 'weight_kg' : key;
        if (PET_FIELDS.has(normalizedKey) && value !== undefined) payload[normalizedKey] = value;
      });
      if (!payload.id && consent === true) payload.data_collection_consent = true;
      return payload;
    };

    const save = async (pet, options = {}) => {
      if (!isSignedIn) {
        const draft = petPayload(pet, false);
        delete draft.id;
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
        status = 'guest_editing';
        return { ok: false, needsAuth: true, draft };
      }

      const existing = pet && pet.id
        ? pets.find((candidate) => String(candidate.id) === String(pet.id))
        : null;
      if (!existing && options.consent !== true) throw new PassportStateError('consent_required');
      if (!existing && pets.length >= maxProfiles) throw new PassportStateError('profile_limit');

      const payload = petPayload(pet, options.consent);
      const previousIds = new Set(pets.map((candidate) => String(candidate.id)));
      status = 'saving';
      try {
        const saved = await api.savePet(payload, existing ? String(existing.version) : null);
        applyState(saved);

        if (options.photoBlob) {
          const canonicalPet = payload.id
            ? pets.find((candidate) => String(candidate.id) === String(payload.id))
            : pets.find((candidate) => !previousIds.has(String(candidate.id)))
              || pets.find((candidate) => candidate.pet_name === payload.pet_name);
          if (!canonicalPet) throw new PassportStateError('photo_pet_missing');
          status = 'photo_uploading';
          const uploadResult = await api.createPhotoUpload(canonicalPet.id, options.photoExtension);
          const upload = uploadResult && uploadResult.data;
          if (!upload || !upload.photo_id || !upload.path || !upload.upload) {
            throw new PassportStateError('invalid_upload_state');
          }
          await api.uploadPhoto(upload.upload, options.photoBlob);
          applyState(await api.finalizePhoto(canonicalPet.id, upload.photo_id, upload.path));
        }

        sessionStorage.removeItem(DRAFT_KEY);
        return snapshot();
      } catch (error) {
        if (error && error.isConflict) {
          status = 'conflict';
          conflict = { local: { ...pet }, server: existing || null };
        } else {
          status = 'error';
        }
        throw error;
      }
    };

    const useSavedVersion = () => {
      conflict = null;
      status = 'ready';
      return snapshot();
    };

    const retryConflict = async (options = {}) => {
      if (!conflict) throw new PassportStateError('conflict_missing');
      const local = conflict.local;
      conflict = null;
      status = 'ready';
      return save(local, options);
    };

    const remove = async (petId) => {
      if (!isSignedIn) return { ok: false, needsAuth: true };
      const existing = pets.find((candidate) => String(candidate.id) === String(petId));
      if (!existing) throw new PassportStateError('pet_missing');
      status = 'deleting';
      try {
        applyState(await api.deletePet(existing.id, existing.version));
        return snapshot();
      } catch (error) {
        status = error && error.isConflict ? 'conflict' : 'error';
        throw error;
      }
    };

    return { load, remove, retryConflict, save, snapshot, useSavedVersion };
  };

  window.FurFarPassport = { createStore, PassportStateError };
})();

document.addEventListener('DOMContentLoaded', async () => {
  const root = document.querySelector('[data-pet-passport]');
  if (!root || !window.FurFarApi || !window.FurFarPassport) return;
  const form = root.querySelector('[data-profile-form]');
  if (!form) return;

  const isSignedIn = root.dataset.customerSignedIn === 'true';
  const loginUrl = root.dataset.loginUrl || document.body.dataset.loginUrl || '/customer_authentication/login';
  const returnTo = root.dataset.returnTo || `${window.location.pathname}${window.location.search}`;
  const maxProfiles = 10;
  const draftKey = 'furAndFarPetPassportDraftV3';
  const store = window.FurFarPassport.createStore({
    api: window.FurFarApi,
    isSignedIn,
    sessionStorage,
    maxProfiles,
  });

  const status = root.querySelector('[data-profile-status]');
  const loading = root.querySelector('[data-passport-loading]');
  const card = root.querySelector('[data-passport-card]');
  const empty = root.querySelector('[data-passport-empty]');
  const list = root.querySelector('[data-profile-list]');
  const deleteButton = root.querySelector('[data-delete-profile]');
  const newProfileButton = root.querySelector('[data-new-profile]');
  const profileCount = root.querySelector('[data-profile-count]');
  const photoInput = form.elements.pet_photo;
  const consentInput = form.elements.data_collection_consent;
  const savedPassports = root.querySelector('[data-saved-passports]');
  const uploadPreview = root.querySelector('[data-upload-preview]');
  const accountPrompt = root.querySelector('[data-account-prompt]');
  const accountPromptLogin = root.querySelector('[data-account-prompt-login]');
  const promptPetName = root.querySelector('[data-prompt-pet-name]');
  const saveSuccess = root.querySelector('[data-save-success]');
  const conflictPanel = root.querySelector('[data-passport-conflict]');
  const legacyPanel = root.querySelector('[data-legacy-import]');
  const legacySummary = root.querySelector('[data-legacy-import-summary]');
  const legacyStatus = root.querySelector('[data-legacy-import-status]');
  const legacyConfirm = root.querySelector('[data-legacy-import-confirm]');
  const legacyDismiss = root.querySelector('[data-legacy-import-dismiss]');
  const stepTitle = root.querySelector('[data-step-title]');
  const submitButton = form.querySelector('[type="submit"]');
  const stepTitles = {
    1: 'Start your companion’s profile',
    2: 'Upload a passport photo',
    3: 'Enter pet information',
    4: 'Save My Passport',
  };

  let preparedPhotoUrl = '';
  let preparedPhotoBlob = null;
  let profiles = [];
  let currentStep = 1;

  const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;',
  })[character]);

  const photoUrl = (value) => {
    const source = value && typeof value === 'object' ? value.url : value;
    const normalized = String(source || '').trim();
    if (/^data:image\/jpeg;base64,[a-z0-9+/=\s]+$/i.test(normalized)) return normalized.replace(/\s+/g, '');
    try {
      const url = new URL(normalized);
      return url.protocol === 'https:' ? url.href : '';
    } catch (error) {
      return '';
    }
  };

  const prettyDate = (value) => value
    ? new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(`${value}T00:00:00`))
    : '—';

  const setStatus = (message) => {
    if (status) status.textContent = message || '';
  };

  const setBusy = (busy) => {
    if (submitButton) submitButton.disabled = busy;
    if (deleteButton) deleteButton.disabled = busy;
    if (newProfileButton) newProfileButton.disabled = busy || profiles.length >= maxProfiles;
  };

  const authLoginHref = () => {
    try {
      const url = new URL(loginUrl, window.location.origin);
      url.searchParams.set('return_to', returnTo);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (error) {
      const separator = loginUrl.includes('?') ? '&' : '?';
      return `${loginUrl}${separator}return_to=${encodeURIComponent(returnTo)}`;
    }
  };

  const showAccountPrompt = (petName) => {
    if (!accountPrompt) return;
    if (promptPetName) promptPetName.textContent = petName || 'your pet';
    accountPrompt.hidden = false;
  };

  const setStep = (step) => {
    currentStep = Math.min(4, Math.max(1, step));
    root.querySelectorAll('[data-step]').forEach((panel) => {
      panel.hidden = Number(panel.dataset.step) !== currentStep;
    });
    root.querySelectorAll('[data-step-dot]').forEach((dot) => {
      const index = Number(dot.dataset.stepDot);
      dot.classList.toggle('is-active', index === currentStep);
      dot.classList.toggle('is-complete', index < currentStep);
    });
    if (stepTitle) stepTitle.textContent = stepTitles[currentStep];
  };

  const setPhotoFrame = (frame, source, alt, emptyMarkup) => {
    if (!frame) return;
    const sourceUrl = photoUrl(source);
    frame.replaceChildren();
    if (sourceUrl) {
      const image = document.createElement('img');
      image.src = sourceUrl;
      image.alt = alt;
      frame.appendChild(image);
    } else {
      frame.insertAdjacentHTML('afterbegin', emptyMarkup);
    }
  };

  const renderUploadPreview = (source) => setPhotoFrame(
    uploadPreview && uploadPreview.querySelector('.ff-passport__iphone-screen'),
    source,
    'Selected pet passport photo',
    '<span class="ff-passport__camera-icon">＋</span><small>Your pet photo</small>',
  );

  const showProfile = (profile) => {
    if (!profile) return;
    savedPassports.hidden = false;
    empty.hidden = true;
    card.hidden = false;
    root.querySelector('[data-card-name]').textContent = profile.pet_name;
    root.querySelector('[data-card-breed]').textContent = [profile.pet_type, profile.breed].filter(Boolean).join(' · ');
    root.querySelector('[data-card-birthday]').textContent = prettyDate(profile.birthday);
    root.querySelector('[data-card-weight]').textContent = profile.weight_kg ? `${profile.weight_kg} kg` : '—';
    root.querySelector('[data-card-harness]').textContent = profile.harness_size || profile.size_category || '—';
    root.querySelector('[data-card-style]').textContent = profile.travel_style || '—';
    root.querySelector('[data-card-status]').textContent = profile.passport_status || 'Ready to roam';
    root.querySelector('[data-card-likes]').textContent = profile.likes || '—';
    root.querySelector('[data-card-notes]').textContent = profile.travel_notes || 'No special travel notes yet.';
    root.querySelector('[data-card-id]').textContent = String(profile.id || '').toUpperCase();
    const catBed = root.querySelector('[data-cat-bed]');
    if (catBed) catBed.hidden = String(profile.pet_type || '').toLowerCase() !== 'cat';
    setPhotoFrame(
      root.querySelector('.ff-passport__photo'),
      profile.photo,
      `${profile.pet_name || 'Pet'} passport photo`,
      '<span>Photo</span>',
    );
  };

  const renderList = () => {
    profileCount.textContent = `${profiles.length} of ${maxProfiles} profiles`;
    newProfileButton.disabled = profiles.length >= maxProfiles;
    newProfileButton.setAttribute('aria-disabled', String(profiles.length >= maxProfiles));
    list.innerHTML = profiles.map((profile) => `
      <button type="button" data-profile-id="${escapeHtml(profile.id)}">
        <span>${escapeHtml(profile.pet_name)}</span>
        <small>${escapeHtml([profile.breed, profile.weight_kg ? `${profile.weight_kg} kg` : ''].filter(Boolean).join(' · ') || profile.travel_style || 'Traveller')}</small>
      </button>
    `).join('');
    list.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', () => loadProfile(button.dataset.profileId));
    });
  };

  const fillForm = (profile) => {
    form.reset();
    const fields = { ...profile, weight: profile.weight_kg || profile.weight || '' };
    Object.entries(fields).forEach(([field, value]) => {
      if (form.elements[field] && field !== 'photo' && field !== 'data_collection_consent') {
        form.elements[field].value = value == null ? '' : value;
      }
    });
    form.elements.profile_id.value = profile.id || '';
    preparedPhotoUrl = photoUrl(profile.photo);
    preparedPhotoBlob = null;
    renderUploadPreview(preparedPhotoUrl);
  };

  const loadProfile = (id) => {
    const profile = profiles.find((candidate) => String(candidate.id) === String(id));
    if (!profile) return;
    fillForm(profile);
    deleteButton.hidden = false;
    showProfile(profile);
    setStep(4);
    setStatus(`Editing ${profile.pet_name}`);
  };

  const resetForm = () => {
    form.reset();
    form.elements.profile_id.value = '';
    preparedPhotoUrl = '';
    preparedPhotoBlob = null;
    renderUploadPreview('');
    deleteButton.hidden = true;
    if (saveSuccess) saveSuccess.hidden = true;
    if (conflictPanel) conflictPanel.hidden = true;
    setStep(1);
    if (profiles[0]) showProfile(profiles[0]);
    else {
      card.hidden = true;
      empty.hidden = false;
      savedPassports.hidden = true;
    }
  };

  const buildPet = () => {
    const data = Object.fromEntries(new FormData(form).entries());
    const petName = String(data.pet_name || '').trim();
    if (!petName) throw new window.FurFarPassport.PassportStateError('name_required');
    const pet = {
      pet_name: petName,
      pet_type: data.pet_type,
      birthday: data.birthday || null,
      breed: data.breed || null,
      weight_kg: data.weight || null,
      size_category: data.size_category || null,
      harness_size: data.harness_size || null,
      clothing_size: data.clothing_size || null,
      chest_cm: data.chest_cm || null,
      neck_cm: data.neck_cm || null,
      home_town: data.home_town || null,
      travel_style: data.travel_style || null,
      travel_bed: data.travel_bed || null,
      passport_status: data.passport_status || null,
      likes: data.likes || null,
      travel_notes: data.travel_notes || null,
    };
    if (data.profile_id) pet.id = String(data.profile_id);
    return pet;
  };

  const dataUrlToBlob = (value) => {
    const [header, encoded] = String(value || '').split(',');
    if (!/^data:image\/jpeg;base64$/i.test(header) || !encoded) throw new Error('image-processing-failed');
    const binary = atob(encoded);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return new Blob([bytes], { type: 'image/jpeg' });
  };

  const syncFromStore = () => {
    profiles = store.snapshot().pets;
    renderList();
  };

  root.querySelectorAll('[data-next-step]').forEach((button) => {
    button.addEventListener('click', () => {
      if (currentStep === 1 && !String(form.elements.pet_name.value || '').trim()) {
        setStatus('Please enter a pet name to continue.');
        form.elements.pet_name.focus();
        return;
      }
      setStep(currentStep + 1);
    });
  });
  root.querySelectorAll('[data-prev-step]').forEach((button) => {
    button.addEventListener('click', () => setStep(currentStep - 1));
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (saveSuccess) saveSuccess.hidden = true;
    if (conflictPanel) conflictPanel.hidden = true;
    let pet;
    try {
      pet = buildPet();
      setBusy(true);
      setStatus(isSignedIn ? 'Saving securely to your account…' : 'Keeping your draft in this tab…');
      const result = await store.save(pet, {
        consent: Boolean(consentInput && consentInput.checked),
        photoBlob: preparedPhotoBlob,
        photoExtension: preparedPhotoBlob ? 'jpg' : undefined,
      });
      if (result.needsAuth) {
        setStatus(`Sign in to save ${pet.pet_name}'s Passport to your Fur & Far account.`);
        showAccountPrompt(pet.pet_name);
        return;
      }
      syncFromStore();
      const canonical = pet.id
        ? profiles.find((candidate) => String(candidate.id) === String(pet.id))
        : profiles.find((candidate) => candidate.pet_name === pet.pet_name);
      if (canonical) loadProfile(canonical.id);
      preparedPhotoBlob = null;
      sessionStorage.removeItem(draftKey);
      setStatus('');
      if (saveSuccess) {
        saveSuccess.hidden = false;
        saveSuccess.textContent = `Passport Saved ✓ ${pet.pet_name}'s profile is available across your devices.`;
      }
    } catch (error) {
      syncFromStore();
      if (error.code === 'consent_required') {
        setStatus('Confirm Passport storage before saving this profile.');
        if (consentInput) consentInput.focus();
      } else if (error.code === 'profile_limit') {
        setStatus('This account already has the maximum of 10 pet profiles.');
      } else if (error.code === 'name_required') {
        setStep(1);
        setStatus('Please enter a pet name.');
      } else if (error.isConflict) {
        setStatus('This Passport changed on another device. Choose which version to keep.');
        if (conflictPanel) conflictPanel.hidden = false;
      } else {
        setStatus('We could not save this Passport. Your form is still here; please try again.');
      }
    } finally {
      setBusy(false);
    }
  });

  deleteButton.addEventListener('click', async () => {
    const id = form.elements.profile_id.value;
    if (!id) {
      showAccountPrompt(form.elements.pet_name.value);
      return;
    }
    setBusy(true);
    setStatus('Deleting Passport…');
    try {
      await store.remove(id);
      syncFromStore();
      resetForm();
      setStatus('Passport deleted.');
    } catch (error) {
      setStatus(error.isConflict
        ? 'This Passport changed on another device. Reload it before deleting.'
        : 'We could not delete this Passport. Please try again.');
    } finally {
      setBusy(false);
    }
  });

  photoInput.addEventListener('change', async () => {
    if (!photoInput.files[0]) return;
    try {
      if (!window.FurAndFarImage) throw new Error('image-processing-failed');
      setStatus('Preparing your photo…');
      preparedPhotoUrl = await window.FurAndFarImage.prepare(photoInput.files[0], {
        maxInputBytes: 10 * 1024 * 1024,
        maxDimension: 2048,
        maxDataBytes: 2 * 1024 * 1024,
      });
      preparedPhotoBlob = dataUrlToBlob(preparedPhotoUrl);
      renderUploadPreview(preparedPhotoUrl);
      setStatus('Photo ready.');
    } catch (error) {
      preparedPhotoUrl = '';
      preparedPhotoBlob = null;
      setStatus(error.message === 'photo-too-large'
        ? 'Please choose a photo no larger than 10 MB.'
        : 'This photo could not be prepared. Try a JPG, PNG, WebP or iPhone photo.');
    }
  });

  newProfileButton.addEventListener('click', resetForm);
  if (accountPromptLogin) accountPromptLogin.href = authLoginHref();
  if (accountPrompt) {
    accountPrompt.querySelectorAll('[data-account-prompt-close]').forEach((button) => {
      button.addEventListener('click', () => { accountPrompt.hidden = true; });
    });
    accountPrompt.addEventListener('click', (event) => {
      if (event.target === accountPrompt) accountPrompt.hidden = true;
    });
  }
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && accountPrompt && !accountPrompt.hidden) accountPrompt.hidden = true;
  });

  const useSavedButton = root.querySelector('[data-conflict-use-server]');
  if (useSavedButton) useSavedButton.addEventListener('click', () => {
    const serverPet = store.snapshot().conflict && store.snapshot().conflict.server;
    const snapshot = store.useSavedVersion();
    profiles = snapshot.pets;
    renderList();
    if (serverPet) loadProfile(serverPet.id);
    if (conflictPanel) conflictPanel.hidden = true;
    setStatus('Using the version already saved to your account.');
  });
  const retryButton = root.querySelector('[data-conflict-retry]');
  if (retryButton) retryButton.addEventListener('click', async () => {
    setBusy(true);
    setStatus('Saving your version…');
    try {
      await store.retryConflict({ consent: true });
      syncFromStore();
      if (conflictPanel) conflictPanel.hidden = true;
      setStatus('Your version is now saved.');
    } catch (error) {
      setStatus('The Passport changed again. Review the saved version before retrying.');
    } finally {
      setBusy(false);
    }
  });

  const legacyStorageKey = root.dataset.legacyStorageKey || '';
  if (isSignedIn && legacyStorageKey && legacyPanel && window.FurFarLegacyImport) {
    try {
      const legacy = await window.FurFarLegacyImport.inspect({
        storage: localStorage,
        suffix: legacyStorageKey,
      });
      if (legacy && (legacy.summary.pets || legacy.summary.items)) {
        legacyPanel.hidden = false;
        if (legacySummary) {
          legacySummary.textContent = `${legacy.summary.pets} Pet Passport${legacy.summary.pets === 1 ? '' : 's'} and ${legacy.summary.items} wishlist item${legacy.summary.items === 1 ? '' : 's'} are ready to import.`;
        }
      }
    } catch (error) {
      legacyPanel.hidden = false;
      if (legacyStatus) legacyStatus.textContent = 'Existing browser data was found but could not be safely read. Nothing has been changed.';
      if (legacyConfirm) legacyConfirm.disabled = true;
    }
  }
  if (legacyDismiss) legacyDismiss.addEventListener('click', () => {
    legacyPanel.hidden = true;
  });
  if (legacyConfirm) legacyConfirm.addEventListener('click', async () => {
    legacyConfirm.disabled = true;
    if (legacyDismiss) legacyDismiss.disabled = true;
    if (legacyStatus) legacyStatus.textContent = 'Importing and verifying your account data…';
    try {
      const imported = await window.FurFarLegacyImport.commit({
        api: window.FurFarApi,
        storage: localStorage,
        suffix: legacyStorageKey,
      });
      await store.load();
      syncFromStore();
      resetForm();
      root.removeAttribute('data-legacy-storage-key');
      if (legacyStatus) legacyStatus.textContent = `${imported.summary.pets} Pet Passport${imported.summary.pets === 1 ? '' : 's'} imported and verified.`;
      setTimeout(() => { legacyPanel.hidden = true; }, 2500);
    } catch (error) {
      if (legacyStatus) legacyStatus.textContent = 'The import could not be verified, so your browser copy has been kept. Please try again.';
      legacyConfirm.disabled = false;
      if (legacyDismiss) legacyDismiss.disabled = false;
    }
  });

  if (loading) loading.hidden = !isSignedIn;
  try {
    await store.load();
    syncFromStore();
    resetForm();
    let draft = null;
    try {
      draft = JSON.parse(sessionStorage.getItem(draftKey) || 'null');
    } catch (error) {
      sessionStorage.removeItem(draftKey);
    }
    if (draft && draft.pet_name) {
      fillForm(draft);
      setStep(4);
      setStatus(isSignedIn
        ? 'Your draft is ready. Confirm Passport storage to save it to your account.'
        : 'Your draft is kept in this tab until you sign in.');
    }
  } catch (error) {
    setStatus('We could not load your saved Passports. Please retry in a moment.');
  } finally {
    if (loading) loading.hidden = true;
  }
});
